import * as Yup from 'yup';

import { FormProvider, useForm } from 'react-hook-form';
import { Stack, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AuthCard } from '@authentication/components/AuthCard';
import { JButton } from '@atoms/JButton';
import JTextFieldForm from '@atoms/JTextFieldForm';
import Paths from '@/routes/paths';
import useJSnackbar from '@/hooks/useJSnackbar';
import useResetPasswordMutation from '@authentication/mutations/resetPasswordMutation';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

const initialValues = {
  newPassword: '',
  confirmPassword: '',
};

const ResetPasswordCard = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { showSnackbar } = useJSnackbar();
  const {
    mutate: resetPassword,
    isError,
    isSuccess,
  } = useResetPasswordMutation();
  const token = searchParams.get('token');
  const resetPasswordSchema = Yup.object().shape({
    newPassword: Yup.string().required('required'),
    confirmPassword: Yup.string()
      .required('required')
      .test(
        'passwords-match',
        t('PASSWORD_MATCH') ?? '',
        function (value, { parent }) {
          return parent.newPassword === value;
        },
      ),
  });

  const methods = useForm<{ newPassword: string; confirmPassword: string }>({
    mode: 'onChange',
    defaultValues: useMemo(() => initialValues, [initialValues]),
    resolver: yupResolver(resetPasswordSchema),
  });

  const { formState, handleSubmit } = methods;
  const { isDirty, isValid } = formState;

  const onSubmit = handleSubmit((data) => {
    if (!token) return;
    resetPassword({ data: { newPassword: data.newPassword }, token });
  });

  useEffect(() => {
    if (!isSuccess) return;
    showSnackbar(t('SUCCES_MSG'), 'success');
    navigate(Paths.LoginPage, { replace: true });
  }, [isSuccess]);

  useEffect(() => {
    if (!isError) return;
    showSnackbar(t('API_ERROR'), 'error');
  }, [isError]);

  return (
    <AuthCard title={t('CHANGE_YOUR_PW')}>
      <>
        <FormProvider {...methods}>
          <form onSubmit={onSubmit} autoComplete="off">
            <Stack gap={1}>
              <JTextFieldForm
                name="newPassword"
                data-qa="newPassword"
                fullWidth
                noBorder
                label={t('PASSWORD_CONFIRM')}
              />

              <JTextFieldForm
                name="confirmPassword"
                data-qa="confirmPassword"
                fullWidth
                noBorder
                label={t('PASSWORD')}
              />

              <JButton
                data-qa="login-button"
                variant="contained"
                color="primary"
                type="submit"
                guttertop
                disabled={!isValid || !isDirty}
              >
                {t('CONTINUE')}
              </JButton>
            </Stack>
          </form>
        </FormProvider>
        <Typography variant="caption">{t('FORGOT_PASSWORD_TIP')}</Typography>
      </>
    </AuthCard>
  );
};

export default ResetPasswordCard;
